import { Component } from 'react';
import * as React from 'react';
import * as ReactHighcharts from 'react-highcharts';
import { Row, Col, FormControl, Button, Dropdown, ControlLabel, FormGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';

interface IAssetsAndGrantsProps {
  displayFilters?: boolean;
  displayAssets?: boolean;
  displayGrants?: boolean;
  state?: string;
  stateLabel?: string;
  displayClickHere?: boolean;
  onChange?: (filters) => void;
}

interface IAssetsAndGrantsState {
  fmvSeries: any[];
  disbursementSeries: any[];
  categories: any[];
  minY2: number;
  maxY2: number;
  minY1: number;
  maxY1: number;
  render: boolean;
  city: string;
  state: IStateOption;
  county: string;
  zip: string;
  dateStart: string;
  stateLabel: string;
  stateData: IStateData[];
  cities: string[];
  states: any[];
  counties: string[];
  filtersLoading: boolean;
}

interface IStateData {
  id: string;
  stateLabel: string;
  county: string;
  city: string;
}

export interface IStateOption {
  id: string;
  stateLabel: string;
}

export class AssetsAndGrants extends Component<IAssetsAndGrantsProps, IAssetsAndGrantsState> {
  state: IAssetsAndGrantsState = {
    fmvSeries: [],
    disbursementSeries: [],
    categories: [],
    minY2: undefined,
    maxY2: undefined,
    maxY1: undefined,
    minY1: undefined,
    render: false,
    city: '',
    state: { id: '', stateLabel: '' },
    county: '',
    zip: '',
    dateStart: '',
    stateLabel: '',
    stateData: [],
    cities: [],
    states: [],
    counties: [],
    filtersLoading: false,
  };

  componentWillReceiveProps(nextProps) {
    if (!this.propsEquality(this.props, nextProps)) {
      this.setState({ render: !this.state.render }, () => {
        return nextProps;
      });
    }

    return this.props;
  }

  propsEquality(currentProps, nextProps) {
    return (
      nextProps.city === currentProps.city &&
      nextProps.state === currentProps.state &&
      nextProps.county === currentProps.county &&
      nextProps.zip === currentProps.zip &&
      nextProps.dateStart === currentProps.dateStart
    );
  }

  componentWillMount() {
    this.setSeries(this.props.state, this.props.stateLabel);
    if (this.props.displayFilters) {
      this.loadStateFilters();
    }
  }

  public setSeries(propsState?: string, propStateLabel?: string) {
    const { city, county, zip, dateStart } = this.state;
    let { state } = this.state;

    if (propsState) {
      const incomingState = { id: propsState, stateLabel: propStateLabel };
      state = incomingState;
    }

    const stateLabel = propStateLabel || this.state.stateLabel;

    const filters = { city, county, state, zip, dateStart, stateLabel };
    if (this.props.onChange) {
      this.props.onChange(filters);
    }

    const givTrendFilterBody = {
      action: 'getOptimizedReturnData',
      request: { assetLevel: 0, state: state.id, name: '', monthNumber: '12', city, county, zip, dateStart: dateStart },
    };
    fetch('https://kbqk6mximj.execute-api.us-east-1.amazonaws.com/Test/foundationmark-requests', {
      method: 'POST',
      body: JSON.stringify(givTrendFilterBody),
    })
      .then((response) => {
        return response.text();
      })
      .then((text) => {
        text = text.replace(/u0026/g, '&');
        text = text.replace(/\\/g, '');
        return JSON.parse(text);
      })
      .then((json) => {
        // Helper function to get year and month from date
        const getYearMonth = (dateStr) => {
          const date = new Date(dateStr);
          return { year: date.getFullYear(), month: date.getMonth() + 1 };
        };

        // Step 1: Group data by year
        const groupedByYear = json.reduce((acc, item) => {
          const [monthStr, yearStr] = item.indexDate.split(', ');
          const monthParsed = new Date(Date.parse(`${monthStr} 1`)).getMonth(); // Get month index from month name

          // Create the date object
          const date = new Date(yearStr, monthParsed, 1);
          const { year, month } = getYearMonth(date);
          const newItem = { ...item, date };
          if (!acc[year]) acc[year] = [];
          acc[year].push({ ...newItem, month });
          return acc;
        }, {});

        // Step 2: For each year, find the last month or latest available month
        const currentYear = new Date().getFullYear();
        const filteredData = Object.keys(groupedByYear).map((year) => {
          const items = groupedByYear[year];
          if (parseInt(year) === currentYear) {
            // For current year, get the latest available month
            return items.sort((a, b) => b.date - a.date)[0];
          } else {
            // For past years, get the last month of the year
            const lastMonth = Math.max(...items.map((item) => item.month));
            return items.find((item) => item.month === lastMonth);
          }
        });

        const fmvSeries = [];
        const disbursementSeries = [];
        const categories = [];

        let minY2 = undefined;
        let maxY2 = undefined;
        let minY1 = undefined;
        let maxY1 = undefined;

        for (const period of filteredData) {
          if (period.fiscalYear == 2008) {
            continue;
          }
          const givPoint = { y: period.fairMarketValue };
          const sixtyFortyPoint = { y: period.disbursements };
          fmvSeries.push(givPoint);
          disbursementSeries.push(sixtyFortyPoint);
          categories.push(period.indexDate);

          if (!minY2) {
            minY2 = period.disbursements;
          } else {
            if (period.disbursements < minY2) {
              minY2 = period.disbursements;
            }
          }

          if (!maxY2) {
            maxY2 = period.disbursements;
          } else {
            if (maxY2 < period.disbursements) {
              maxY2 = period.disbursements;
            }
          }

          if (!minY1) {
            minY2 = period.disbursements;
          } else {
            if (period.disbursements < minY1) {
              minY1 = period.disbursements;
            }
          }

          if (!maxY1) {
            maxY2 = period.disbursements;
          } else {
            if (maxY1 < period.disbursements) {
              maxY2 = period.disbursements;
            }
          }
        }

        maxY2 = maxY2 * 2;
        minY2 = minY2 * 0.6;
        maxY1 = maxY1 * 1.2;
        minY1 = minY1 * 0.9;

        const disbursementsIndex = disbursementSeries.length - 1;
        disbursementSeries[disbursementsIndex] = {
          x: disbursementSeries[disbursementsIndex].x,
          y: disbursementSeries[disbursementsIndex].y,
          dataLabels: {
            enabled: true,
            align: 'right',
            x: 3,
            verticalAlign: 'top',
            overflow: true,
            crop: false,
            format: '{series.name}',
          },
        };

        fmvSeries[fmvSeries.length - 1] = {
          x: fmvSeries[fmvSeries.length - 1].x,
          y: fmvSeries[fmvSeries.length - 1].y,
          dataLabels: {
            enabled: true,
            align: 'right',
            x: 3,
            verticalAlign: 'top',
            overflow: true,
            crop: false,
            format: '{series.name}',
            style: {
              fontSize: '14px',
            },
          },
        };

        const stateLabel = propStateLabel || this.state.stateLabel;

        this.setState({ fmvSeries, disbursementSeries, categories, minY2, maxY2, minY1, maxY1, stateLabel });
      });
  }

  getGrantmakingConfig(fmvSeries, disbursementSeries, categories) {
    const { maxY2 } = this.state;
    const { city, county, zip, stateLabel } = this.state;
    const { displayAssets, displayGrants, displayClickHere } = this.props;
    //NOTE: Switch title if any are not empty
    let displayStateLabel = undefined;
    if (!stateLabel) {
      displayStateLabel = '';
    } else {
      displayStateLabel = stateLabel;
    }

    const title =
      city !== ''
        ? city
        : (county !== '' ? county : displayStateLabel !== '' ? displayStateLabel : zip !== '' ? zip : 'U.S.') + ' Foundations';
    const subtitle = displayClickHere
      ? '<b>Click <a style="color: #333" href="#/grants">here</a> for more interactive visualizations</b>'
      : '';
    const series = [];
    const yAxis = [];

    if (displayAssets) {
      series.push({
        name: 'Total Assets',
        type: 'column',
        colorByPoint: false,
        color: '#8FAADC',
        borderColor: '#205C8F',
        //shadow: true,
        yAxis: 'y_axis_1',
        fillOpacity: 0.1,
        lineColor: '#8FAADC',
        lineWidth: 4,
        data: fmvSeries,
      });

      yAxis.push({
        id: 'y_axis_1',
        //min: minY1,
        //max: maxY1,
        title: { text: 'Assets*', style: { fontWeight: 'bold' } },
        labels: {
          format: '<b>${value:,.0f} mm</b>',
        },
        style: {
          fontWeight: 'bold',
        },
      });
    }

    if (displayGrants) {
      series.push({
        name: 'Grants and Expenses',
        type: 'line',
        colorByPoint: false,
        color: '#808080',
        yAxis: 'y_axis_2',
        dashStyle: 'ShortDash',
        lineWidth: 4,
        fillOpacity: 0.1,
        lineColor: '#808080',
        data: disbursementSeries,
        label: {
          enabled: true,
        },
      });

      yAxis.push({
        id: 'y_axis_2',
        title: { text: 'Grants, Gifts and Operating Expenses*', style: { fontWeight: 'bold' } },
        opposite: yAxis.length === 1,
        max: maxY2,
        //min: minY2,
        labels: {
          format: '<b>${value:,.0f} mm</b>',
        },
      });
    }

    return {
      chart: {},
      xAxis: {
        categories: categories,
      },
      title: {
        text: title,
      },
      subtitle: {
        text: subtitle,
        style: {
          fontSize: '1.6rem',
          color: '#00B050',
        },
        floating: true,
        x: 150,
        y: 100,
        align: 'left',
        useHTML: true,
      },
      yAxis: yAxis,
      legend: {
        enabled: true,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          marker: {
            enabled: false,
          },
        },
      },
      tooltip: {
        pointFormat: '{series.name}: <b>${point.y:,.1f} mm</b><br/>',
        shared: true,
      },
      series: series,
    };
  }

  public clearSeries() {
    const { stateData } = this.state;

    let initStates = [];
    let initCounties = [];
    let initCities = [];

    for (let city of stateData) {
      let index = initStates.findIndex((value) => value.id == city.id);
      if (index == -1) {
        initStates.push({ id: city.id, stateLabel: city.stateLabel });
      }

      initCounties.push(city.county);
      initCities.push(city.city);
    }

    const states = initStates;
    const counties = initCounties.filter((n, i) => initCounties.indexOf(n) === i);
    const cities = initCities.filter((n, i) => initCities.indexOf(n) === i);

    this.setState(
      { city: '', state: { id: '', stateLabel: '' }, county: '', zip: '', dateStart: undefined, states, counties, cities, stateLabel: '' },
      () => this.setSeries(),
    );
  }

  public loadStateFilters() {
    this.setState({ filtersLoading: true });

    const filterBody = { action: 'getStateFilters', request: {} };

    fetch('https://kbqk6mximj.execute-api.us-east-1.amazonaws.com/Test/foundationmark-requests', {
      method: 'POST',
      body: JSON.stringify(filterBody),
    })
      .then((response) => {
        return response.text();
      })
      .then((text) => {
        text = text.replace(/u0026/g, '&');
        text = text.replace(/\\/g, '');
        return JSON.parse(text);
      })
      .then((json) => {
        const states = json.map((state) => {
          return { id: state.id, stateLabel: state.stateLabel };
        });
        this.setState({ stateData: states });
      });
  }

  public updateLists(stateId) {
    const { stateData } = this.state;

    let filteredStates = stateData.filter((n) => n.id === stateId);

    const filterBody = { action: 'getCityCountyFilters', request: { state: stateId } };

    fetch('https://kbqk6mximj.execute-api.us-east-1.amazonaws.com/Test/foundationmark-requests', {
      method: 'POST',
      body: JSON.stringify(filterBody),
    })
      .then((response) => {
        return response.text();
      })
      .then((text) => {
        text = text.replace(/u0026/g, '&');
        text = text.replace(/\\/g, '');
        return JSON.parse(text);
      })
      .then((json) => {
        const state = this.state.stateData.find((x) => x.id === stateId);
        const countySet = new Set<string>(json.map((x) => x.county));
        const citySet = new Set<string>(json.map((x) => x.city));

        const counties: string[] = Array.from(countySet);
        const cities: string[] = Array.from(citySet);

        this.setState({ counties, cities, state, stateLabel: state.stateLabel });
      });
  }

  changeCity(e) {
    this.setState({ city: e.target.value }, () => this.setSeries());
  }

  changeCounty(county) {
    const selectedCounty = county.target.value;

    this.setState({ county: selectedCounty }, () => this.setSeries());
  }

  changeState(e) {
    const stateId = e.target.value;

    this.updateLists(stateId);
  }

  changeZip(e) {
    const zip = e.target.value;
    this.setState({ zip }, () => this.setSeries());
  }

  changeDateStart(e) {
    const date = e.target.value;
    this.setState({ dateStart: date }, () => this.setSeries());
  }

  render() {
    return (
      <section>
        {this.props.displayFilters && (
          <section style={{ padding: '1.0rem' }}>
            <Row>
              <Col sm={12} md={3}>
                <FormGroup controlId="formControlsSelect">
                  <ControlLabel>State:</ControlLabel>
                  <FormControl onChange={this.changeState.bind(this)} componentClass="select" placeholder="select">
                    <option value="">Select a state...</option>
                    {this.state.stateData.map((state) => {
                      return <option value={state.id}>{state.stateLabel}</option>;
                    })}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={12} md={3}>
                <FormGroup controlId="formControlsSelect">
                  <ControlLabel>County:</ControlLabel>
                  <FormControl onChange={this.changeCounty.bind(this)} componentClass="select" placeholder="select">
                    <option value="">Select a county...</option>
                    {this.state.counties.map((county) => {
                      return <option value={county}>{county}</option>;
                    })}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={12} md={3}>
                <FormGroup controlId="formControlsSelect">
                  <ControlLabel>City:</ControlLabel>
                  <FormControl onChange={this.changeCity.bind(this)} componentClass="select" placeholder="select">
                    <option value="">Select a city...</option>
                    {this.state.cities.map((city) => {
                      return <option value={city}>{city}</option>;
                    })}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={12} md={2}>
                <label htmlFor="zipcodeInput">Zip Code:</label>
                <FormControl
                  id="zipcodeInput"
                  type="text"
                  value={this.state.zip}
                  placeholder="Enter a ZIP code"
                  onChange={this.changeZip.bind(this)}
                />
              </Col>
              <Col sm={12} md={1} style={{ float: 'right' }}>
                <label htmlFor="clearBtn"></label>
                <Button id="clearBtn" bsStyle="info" onClick={this.clearSeries.bind(this)}>
                  Clear
                </Button>
              </Col>
            </Row>
          </section>
        )}
        <ReactHighcharts
          config={this.getGrantmakingConfig(this.state.fmvSeries, this.state.disbursementSeries, this.state.categories)}
          ref="chart"
        />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/components/visualizations/AssetsAndGrants/index.tsx